/**
 * PoweredByDirt - The "POWERED BY DIRT" credit: a small "Powered by" caption
 * sitting over the DIRT logo (rabbit + DIRT letters).
 *
 * DIRT is the platform's own name; G Brands is the customer brand the app
 * wears. Beta feedback (Oct 2026): "Wanted to add 'Powered by DIRT' and have
 * the rabbit logo somewhere" — the mock-up puts it under the G Brands logo at
 * the top of the side menu, kept small so it never competes with G Brands.
 *
 * The logo artwork already contains the "DIRT" letters, so there is no
 * separate text wordmark. If the file ever fails to load, the image swaps
 * for a plain "DIRT" text fallback so the credit never shows a broken image.
 *
 * @example
 * <PoweredByDirt className="mt-16px" />
 */
import React, { useState } from 'react';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** The DIRT logo (rabbit + DIRT letters), served from `public/brand/`. */
export const DIRT_LOGO_SRC = '/brand/dirt-logo.svg';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface PoweredByDirtProps {
	/** Optional extra classes merged after the base classes */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const PoweredByDirt: React.FC<PoweredByDirtProps> = ({ className = '' }) => {
	/* Flips if the logo fails to load, so a broken image icon never shows. */
	const [logoMissing, setLogoMissing] = useState(false);

	return (
		/* inline-flex + self-center: the block shrinks to the logo's width. */
		<div className={`powered-by-dirt inline-flex flex-col self-center ${className}`} data-ref="atom-powered-by-dirt">
			{/* The logo file carries blank space on its right as wide as the
			    rabbit, so the DIRT letters sit dead-centre in the image — that
			    lines them (and this caption) up under the centred G Brands
			    wordmark. */}
			<span className="powered-by-dirt__caption font-sans text-8px font-semibold uppercase tracking-widest leading-none text-brand-text-muted text-center">
				Powered by
			</span>
			{logoMissing ? (
				<span className="powered-by-dirt__wordmark font-heading text-16px font-bold leading-none tracking-wide text-brand-text text-center mt-2px">
					DIRT
				</span>
			) : (
				/* 106px wide = the 86px artwork plus its right-hand padding.
				   -mt-2px: the artwork has empty space above the DIRT letters
				   (the rabbit's ears are taller), so pull it up to keep the
				   caption snug to the letters. */
				<img
					src={DIRT_LOGO_SRC}
					alt="DIRT"
					className="powered-by-dirt__logo -mt-2px w-[106px] h-auto"
					onError={() => setLogoMissing(true)}
				/>
			)}
		</div>
	);
};

export default PoweredByDirt;
